// =====================================================================
// PendingMediaChecklist — manual re-upload checklist for Kit/Bento
// =====================================================================
// Kit and Bento have no media endpoint, so the dispatch performers
// strip Excerptus-hosted <img> tags and seed `content_*_pending_media`
// rows. This component renders those rows with a download link
// (signed URL, refreshable when expired) and a "Mark uploaded"
// toggle so the user can track progress through their followup.
// =====================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Download, Loader2, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  clearPendingMediaUploaded,
  listPendingMedia,
  markPendingMediaUploaded,
  refreshSignedUrl,
  type PendingMediaRow,
  type PendingMediaScope,
} from "@/lib/pendingMedia";

type Props = {
  scope: PendingMediaScope;
  workspaceId: string;
  parentId: string;
  /** Optionally narrow to a single dispatch's rows. */
  dispatchId?: string;
  /** Compact mode hides the header chrome (for inline drawer use). */
  compact?: boolean;
};

const isUrlExpired = (iso: string | null): boolean => {
  if (!iso) return true;
  return new Date(iso).getTime() <= Date.now() + 60_000; // 1m skew
};

export const PendingMediaChecklist = ({
  scope,
  workspaceId,
  parentId,
  dispatchId,
  compact,
}: Props) => {
  const [rows, setRows] = useState<PendingMediaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const all = await listPendingMedia(scope, workspaceId, parentId);
      setRows(dispatchId ? all.filter((r) => r.dispatch_id === dispatchId) : all);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load pending media");
    } finally {
      setLoading(false);
    }
  }, [scope, workspaceId, parentId, dispatchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const summary = useMemo(() => {
    const pending = rows.filter((r) => !r.uploaded_at).length;
    return { total: rows.length, pending };
  }, [rows]);

  const handleToggle = async (row: PendingMediaRow) => {
    setBusyId(row.id);
    try {
      if (row.uploaded_at) {
        await clearPendingMediaUploaded(scope, row.id);
      } else {
        await markPendingMediaUploaded(scope, row.id);
      }
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (row: PendingMediaRow) => {
    let url = row.signed_url;
    if (!url || isUrlExpired(row.signed_url_expires_at)) {
      setBusyId(row.id);
      try {
        const fresh = await refreshSignedUrl(scope, row);
        url = fresh.signed_url;
        setRows((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not refresh link");
        setBusyId(null);
        return;
      }
      setBusyId(null);
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <p className="text-xs text-muted-foreground">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading pending media…
      </p>
    );
  }
  if (rows.length === 0) return null;

  return (
    <div className="space-y-2 rounded-md border border-border bg-card p-3">
      {!compact ? (
        <div className="flex items-center justify-between gap-2">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">Manual image re-upload</p>
            <p className="text-xs text-muted-foreground">
              This destination doesn't accept image uploads via API. Download each image and
              attach it in the destination's editor, then mark it done.
            </p>
          </div>
          <Badge variant={summary.pending === 0 ? "secondary" : "destructive"}>
            {summary.pending} / {summary.total} pending
          </Badge>
        </div>
      ) : null}
      <ul className="space-y-1.5">
        {rows.map((row) => {
          const done = !!row.uploaded_at;
          return (
            <li
              key={row.id}
              className="flex items-center gap-2 rounded-sm border border-border/60 bg-background p-2 text-xs"
            >
              <button
                type="button"
                onClick={() => handleToggle(row)}
                disabled={busyId === row.id}
                className="text-muted-foreground hover:text-foreground"
                aria-label={done ? "Mark as not uploaded" : "Mark as uploaded"}
              >
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                ) : (
                  <Circle className="h-4 w-4" />
                )}
              </button>
              <span className={done ? "line-through text-muted-foreground" : "font-medium"}>
                {row.filename}
              </span>
              {row.alt_text ? (
                <span className="truncate text-muted-foreground">— {row.alt_text}</span>
              ) : null}
              <Button
                type="button"
                onClick={() => handleDownload(row)}
                variant="ghost"
                size="sm"
                disabled={busyId === row.id}
                className="ml-auto h-7 px-2"
              >
                {busyId === row.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : isUrlExpired(row.signed_url_expires_at) ? (
                  <RefreshCcw className="mr-1 h-3 w-3" />
                ) : (
                  <Download className="mr-1 h-3 w-3" />
                )}
                {isUrlExpired(row.signed_url_expires_at) ? "Refresh" : "Download"}
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PendingMediaChecklist;
